/**
 * Leads (Records) — every lead across the whole pipeline.
 *
 * Complements LeadLoop: the loop is the action-needed queue; this is the
 * full record list with status/search filtering. Rows open the same Lead
 * Detail page the loop uses (/demo/lcc/leads/:leadId).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, UserPlus } from 'lucide-react';
import { LCC_LEAD_STATUSES, lccStatusLabel } from '@oneup/shared';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { lccApi } from '@/lib/lcc-api';
import {
  formatLccLabel,
  formatLccRelative,
  leadDueState,
  LccEmptyState,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
  LccStatusBadge,
} from './lcc-shared';
import { VerifiedCheck } from './record-interaction';

export function LeadsRecordsView() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'leads', status, search],
    queryFn: () => lccApi.leads({ status, q: search || undefined }),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <LccPageHeader
        title="Leads"
        blurb="Every lead across the whole pipeline — open, won, and closed. For the who-needs-contact-next queue, use LeadLoop."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-[280px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads…"
            className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-2.5 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[190px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open only</SelectItem>
            {LCC_LEAD_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {lccStatusLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <span className="text-[12px] text-muted-foreground">
            {data.total} lead{data.total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load leads" error={error} />}
      {!isLoading && error == null && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2.5 font-semibold" aria-label="Verified" />
                  <th className="px-4 py-2.5 font-semibold">Lead</th>
                  <th className="px-4 py-2.5 font-semibold">Contact</th>
                  <th className="px-4 py-2.5 font-semibold">Organization</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Next Step</th>
                  <th className="px-4 py-2.5 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.items ?? []).map((lead) => {
                  const due = leadDueState(lead);
                  return (
                    <tr
                      key={lead.id}
                      className="cursor-pointer transition-colors hover:bg-muted/40"
                      onClick={() => navigate(`/demo/lcc/leads/${lead.id}`)}
                    >
                      <td className="px-3 py-2.5">
                        <VerifiedCheck lead={lead} />
                      </td>
                      <td className="max-w-[300px] px-4 py-2.5">
                        <div className="truncate font-medium">{lead.title}</div>
                        {lead.campaign && (
                          <div className="truncate text-[11px] text-muted-foreground">{lead.campaign}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{lead.contactName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {lead.organization?.name ?? lead.companyName ?? '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <LccStatusBadge status={lead.status} label={lead.statusLabel} />
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{formatLccLabel(lead.source)}</td>
                      <td className="max-w-[260px] px-4 py-2.5">
                        {due.kind === 'closed' ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <>
                            <div
                              className={
                                due.kind === 'overdue'
                                  ? 'font-medium text-destructive'
                                  : due.kind === 'needs_next_step'
                                    ? 'font-medium text-warning-foreground'
                                    : 'text-muted-foreground'
                              }
                            >
                              {due.label}
                            </div>
                            {lead.nextStep && (
                              <div className="truncate text-[11px] text-muted-foreground">{lead.nextStep}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground" title={lead.updatedAt}>
                        {formatLccRelative(lead.updatedAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(data?.items ?? []).length === 0 && (
            <LccEmptyState icon={UserPlus} message="No leads match this filter." />
          )}
        </Card>
      )}
    </div>
  );
}
